import getFixedTimeOptions from './getFixedTimeOptions.js';
import getTimeZoneOptions from './getTimeZoneOptions.js';

const scheduleSchemaFields = [
  {
    key: 'scheduleType',
    name: 'Schedule Type',
    description: 'Whether the schedule runs at fixed times or at regular intervals',
    path: 'arguments.scheduleType',
    dataType: 'list',
    options: [
      { value: 'fixed', label: 'Fixed Times' },
      { value: 'interval', label: 'Interval' }
    ]
  },
  {
    visible: (result) => result?.arguments?.scheduleType === 'fixed',
    parentKey: 'scheduleType',
    key: 'runTimes',
    name: 'Run Times',
    description: 'The times the schedule runs on.',
    path: 'arguments.runTimes',
    dataType: 'list',
    options: getFixedTimeOptions(),
    multiple: true
  },
  {
    visible: (result) => result?.arguments?.scheduleType === 'fixed',
    parentKey: 'scheduleType',
    key: 'timeZone',
    name: 'Time Zone',
    description: 'The time zone the schedule runs in.',
    path: 'arguments.timeZone',
    dataType: 'list',
    options: getTimeZoneOptions(),
    multiple: false
  },
  {
    visible: (result) => result?.arguments?.scheduleType === 'interval',
    parentKey: 'scheduleType',
    key: 'interval',
    name: 'Run Frequency',
    description: 'The number of milliseconds between each run.',
    path: 'arguments.interval',
    dataType: {
      type: 'object',
      properties: {
        format: { type: 'string', enum: ['minutes', 'hours', 'days'] },
        value: { type: 'number' }
      }
    },
    validateValue: function (value) {
      const second = 1000;
      const minute = second * 60;
      const hour = minute * 60;
      const day = hour * 24;

      let valueInMs;
      if (typeof value === 'object') {
        if (value.format === 'minutes') {
          valueInMs = value.value * minute;
        } else if (value.format === 'hours') {
          valueInMs = value.value * hour;
        } else if (value.format === 'days') {
          valueInMs = value.value * day;
        } else if (value.format === 'seconds') {
          valueInMs = value.value * second;
        }
      } else {
        valueInMs = value;
      }

      const minMs = 10 * minute;
      const maxMs = 30 * day;

      if (valueInMs < minMs) {
        return { error: 'Interval must be at least 10 minutes' };
      }
      if (valueInMs > maxMs) {
        return { error: 'Interval must be no more than 30 days' };
      }
    },
    serializeValue: (value) => {
      const minute = 1000 * 60;
      const hour = minute * 60;
      const day = hour * 24;
      if (value < hour) {
        return { format: 'minutes', value: Math.round(value / minute) };
      } else if (value < day) {
        return { format: 'hours', value: Math.round(value / hour) };
      } else {
        return { format: 'days', value: Math.round(value / day) };
      }
    },
    deserializeValue: function (obj) {
      const minute = 1000 * 60;
      const hour = minute * 60;
      const day = hour * 24;
      if (obj.format === 'minutes') {
        return obj.value * minute;
      } else if (obj.format === 'hours') {
        return obj.value * hour;
      } else if (obj.format === 'days') {
        return obj.value * day;
      }
      return null;
    },
    formatLabel: (value) => {
      const minute = 1000 * 60;
      const hour = minute * 60;
      const day = hour * 24;

      if (value < hour) {
        const minutes = Math.round(value / minute);
        return `Every ${minutes} Minute${minutes > 1 ? 's' : ''}`;
      } else if (value < day) {
        const hours = Math.round(value / hour);
        return `Every ${hours} Hour${hours > 1 ? 's' : ''}`;
      } else {
        const days = Math.round(value / day);
        return `Every ${days} Day${days > 1 ? 's' : ''}`;
      }
    },
    minValue: { format: 'minutes', value: 10 },
    maxValue: { format: 'days', value: 30 }
  },
  {
    key: 'days',
    name: 'Run Days',
    description: 'The days of the week the schedule runs on.',
    path: 'arguments.days',
    dataType: 'list',
    options: [
      { value: 0, label: 'Sunday' },
      { value: 1, label: 'Monday' },
      { value: 2, label: 'Tuesday' },
      { value: 3, label: 'Wednesday' },
      { value: 4, label: 'Thursday' },
      { value: 5, label: 'Friday' },
      { value: 6, label: 'Saturday' }
    ],
    multiple: true
  }
];

export default scheduleSchemaFields;
